import {
    faFileInvoiceDollar,
    faFileLines,
    faHeartPulse,
    faLaptopMedical,
    faLeaf,
    faSquarePlus,
    faUpload,
} from "@fortawesome/free-solid-svg-icons"

import {
    COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG,
    OneVerifyEraUploadFileFormat,
} from "@common-shared/config/oneVerify/oneVerifyIntegrations.config"
import {
    OneVerifyIntegrationCategory,
    OneVerifyIntegrationStatus,
} from "@common-shared/types/integrations.types"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"

import type { RawEraUploadCsvRowDTO } from "../dto/rawEraUploadCsvRow.dto"
import type {
    IEraUploadRowDisplayColumnMapping,
    IOneVerifyIntegration,
} from "../types/integrations.types"
import { ISelectTabsItem } from "../types/objects.types"

import advancedMdLogo from "@front-shared/assets/images/icons/integrations/advanced-md-logo.svg"
import athenaHealthLogo from "@front-shared/assets/images/icons/integrations/athenahealth-logo.svg"
import candidHealthLogo from "@front-shared/assets/images/icons/integrations/candid-health-logo.svg"
import healthieLogo from "@front-shared/assets/images/icons/integrations/healthie-logo.svg"

export enum EraUploadBulkUploadStage {
    INITIAL = "initial",
    STAGING = "staging",
    VALIDATING = "validating",
    UPLOADING = "uploading",
    SUCCESS = "success",
    ERROR = "error",
}

export enum EraUploadFileUploadStatus {
    pending = "pending",
    uploading = "uploading",
    succeeded = "succeeded",
    failed = "failed",
}

export enum EraUploadIntegrationTab {
    x12 = "x12",
    csv = "csv",
    uploadedFiles = "uploaded_files",
}

export const INTEGRATIONS_CONFIG = {
    defaultNotifyLeadCopy:
        "Interested? Leave your email and we will notify you when this is ready.",

    get displayCategories(): ISelectTabsItem<OneVerifyIntegrationCategory>[] {
        const categoryLabelMapping =
            COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.categoryLabelMapping

        return [
            {
                value: OneVerifyIntegrationCategory.eraData,
                label: categoryLabelMapping[
                    OneVerifyIntegrationCategory.eraData
                ],
                icon: faFileLines,
            },
            {
                value: OneVerifyIntegrationCategory.ehr,
                label: categoryLabelMapping[OneVerifyIntegrationCategory.ehr],
                icon: faSquarePlus,
            },
        ]
    },

    get eraIntegrations(): IOneVerifyIntegration[] {
        return [
            {
                id: COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload
                    .integrationId,
                name: "Upload ERA",
                category: OneVerifyIntegrationCategory.eraData,
                status: OneVerifyIntegrationStatus.live,
                tagline:
                    "Manually upload ERA remittance data via CSV or raw X12 835 file.",
                description:
                    "Uploading ERA data helps Nirvana improve coverage accuracy, surface denial patterns across payers, and close the loop between eligibility checks and payment outcomes.",
                features: [
                    `Upload raw X12 835 remittance files (${COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload.formatX12ExtensionsLabel()}).`,
                    "Upload CSV files with ERA remittance rows using our template.",
                    "Improved coverage accuracy and recall scores as payment data enriches coverage models.",
                    "Denial pattern visibility across payers and service lines.",
                ],
                markIcon: faUpload,
                markBackgroundColor: NIRVANA_COLORS.brand.greenDark,
            },
            {
                id: "candidHealth",
                name: "Candid Health",
                category: OneVerifyIntegrationCategory.eraData,
                status: OneVerifyIntegrationStatus.comingSoon,
                tagline:
                    "ERA and claims data via Candid's BigQuery Data Share, refreshed nightly.",
                description:
                    "Integrating Candid Health will allow Nirvana to ingest your ERA remittance data automatically — improving accuracy, surfacing denial patterns, and closing the loop between eligibility and payment.",
                features: [
                    "Nightly ERA ingestion from Candid's BigQuery Data Share",
                    "Denial pattern visibility across payers",
                    "Improved coverage accuracy and recall scores",
                    "Automated reconciliation for ERA and claims",
                ],
                markIcon: faFileInvoiceDollar,
                markImageUrl: candidHealthLogo,
                markBackgroundColor: NIRVANA_COLORS.brand.purpleMedium,
            },
        ]
    },

    get ehrIntegrations(): IOneVerifyIntegration[] {
        return [
            {
                id: "athenaHealth",
                name: "Athenahealth",
                category: OneVerifyIntegrationCategory.ehr,
                status: OneVerifyIntegrationStatus.live,
                tagline:
                    "Direct integrations with your EHR to sync patient and appointment data.",
                description:
                    "Integrating your EHR will allow Nirvana to sync patient and appointment data — so you can automate eligibility checks, write coverage results back to the chart, and keep demographics in sync without duplicate entry.",
                features: [
                    "Auto-trigger eligibility checks at appointment creation.",
                    "Coverage results written back to patient chart.",
                    "Patient demographic sync — no duplicate data entry.",
                    "Works with your existing EHR workflow.",
                ],
                markIcon: faLeaf,
                markImageUrl: athenaHealthLogo,
                markImageFit: "contain",
                markBackgroundColor: NIRVANA_COLORS.brand.greenDark,
            },
            {
                id: "advancedMd",
                name: "AdvancedMD",
                category: OneVerifyIntegrationCategory.ehr,
                status: OneVerifyIntegrationStatus.live,
                tagline:
                    "Direct integrations with your EHR to sync patient and appointment data.",
                description:
                    "Integrating your EHR will allow Nirvana to sync patient and appointment data — so you can automate eligibility checks, write coverage results back to the chart, and keep demographics in sync without duplicate entry.",
                features: [
                    "Auto-trigger eligibility checks at appointment creation.",
                    "Coverage results written back to patient chart.",
                    "Patient demographic sync — no duplicate data entry.",
                    "Works with your existing EHR workflow.",
                ],
                markIcon: faLaptopMedical,
                markImageUrl: advancedMdLogo,
                markImageFit: "contain",
                markBackgroundColor: NIRVANA_COLORS.brand.orange,
            },
            {
                id: "healthie",
                name: "Healthie",
                category: OneVerifyIntegrationCategory.ehr,
                status: OneVerifyIntegrationStatus.comingSoon,
                tagline:
                    "Direct integrations with your EHR to sync patient and appointment data.",
                description:
                    "Integrating your EHR will allow Nirvana to sync patient and appointment data — so you can automate eligibility checks, write coverage results back to the chart, and keep demographics in sync without duplicate entry.",
                features: [
                    "Auto-trigger eligibility checks at appointment creation.",
                    "Coverage results written back to patient chart.",
                    "Patient demographic sync — no duplicate data entry.",
                    "Works with your existing EHR workflow.",
                ],
                markIcon: faHeartPulse,
                markImageUrl: healthieLogo,
                markImageFit: "cover",
                markBackgroundColor: NIRVANA_COLORS.brand.stormyPurple,
            },
        ]
    },

    eraUpload: {
        ...COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload,

        validationConcurrency: 3,

        maxHeaderPreviewBytes: 256 * 1024,

        maxPreviewTableRows: 50,

        maxX12StructurePreviewBytes: 256 * 1024,

        x12RequiredSegmentIds: {
            isa: "ISA",
            gs: "GS",
            st: "ST",
        } as const,

        get requiredX12SegmentsLabel(): string {
            const { isa, gs, st } = this.x12RequiredSegmentIds

            return `${isa}, ${gs}, and ${st}`
        },

        csvAccept: ".csv,text/csv,application/csv",

        x12Accept: ".835,.edi,.era,.txt,application/edi-x12,text/plain",

        getFileAcceptForFormat(
            fileFormat: OneVerifyEraUploadFileFormat,
        ): string {
            return fileFormat === OneVerifyEraUploadFileFormat.csv
                ? this.csvAccept
                : this.x12Accept
        },

        get uploadTabs(): {
            key: EraUploadIntegrationTab
            label: string
        }[] {
            return [
                {
                    key: EraUploadIntegrationTab.x12,
                    label: "X12 835 File",
                },
                {
                    key: EraUploadIntegrationTab.csv,
                    label: "CSV",
                },
                {
                    key: EraUploadIntegrationTab.uploadedFiles,
                    label: "Uploaded Files",
                },
            ]
        },

        get csvTemplateUrl(): string {
            const baseAssetsUrl = import.meta.env.VITE_S3_ASSETS_BASE_URL

            return `${baseAssetsUrl}/nirvana-era-upload-template-v1.csv`
        },

        get ruleDisplayTableColumnMapping(): Record<
            Exclude<keyof RawEraUploadCsvRowDTO, "index">,
            IEraUploadRowDisplayColumnMapping
        > {
            return {
                patientFirstName: {
                    header: "Patient first name",
                    valuePath: "patientFirstName",
                    tableWidthClassName: "w-[150px]",
                },
                patientLastName: {
                    header: "Patient last name",
                    valuePath: "patientLastName",
                    tableWidthClassName: "w-[150px]",
                },
                patientDateOfBirth: {
                    header: "Patient DOB",
                    valuePath: "patientDateOfBirth",
                    tableWidthClassName: "w-[130px]",
                },
                memberId: {
                    header: "Member ID",
                    valuePath: "memberId",
                    tableWidthClassName: "w-[130px]",
                },
                payerEntityName: {
                    header: "Payer entity name",
                    valuePath: "payerEntityName",
                    tableWidthClassName: "w-[180px]",
                    tableCellTruncate: true,
                },
                nirvanaPayerId: {
                    header: "Nirvana payer ID",
                    valuePath: "nirvanaPayerId",
                    tableWidthClassName: "w-[140px]",
                },
                claimId: {
                    header: "Claim ID",
                    valuePath: "claimId",
                    tableWidthClassName: "w-[130px]",
                },
                appointmentDate: {
                    header: "Appointment date",
                    valuePath: "appointmentDate",
                    tableWidthClassName: "w-[150px]",
                },
                cptCode: {
                    header: "CPT code",
                    valuePath: "cptCode",
                    tableWidthClassName: "w-[110px]",
                },
                providerNpi: {
                    header: "Provider NPI",
                    valuePath: "providerNpi",
                    tableWidthClassName: "w-[130px]",
                },
                billAmount: {
                    header: "Bill amount",
                    valuePath: "billAmount",
                    tableWidthClassName: "w-[120px]",
                },
                insurancePaidAmount: {
                    header: "Insurance paid",
                    valuePath: "insurancePaidAmount",
                    tableWidthClassName: "w-[130px]",
                },
                allowedAmount: {
                    header: "Allowed amount",
                    valuePath: "allowedAmount",
                    tableWidthClassName: "w-[130px]",
                },
                isInNetwork: {
                    header: "In network",
                    valuePath: "isInNetwork",
                    tableWidthClassName: "w-[110px]",
                },
                isInsurancePrimary: {
                    header: "Primary insurance",
                    valuePath: "isInsurancePrimary",
                    tableWidthClassName: "w-[140px]",
                },
                isInOffice: {
                    header: "In office",
                    valuePath: "isInOffice",
                    tableWidthClassName: "w-[110px]",
                },
                copaymentAmount: {
                    header: "Copayment",
                    valuePath: "copaymentAmount",
                    tableWidthClassName: "w-[110px]",
                },
                coinsuranceAmount: {
                    header: "Coinsurance",
                    valuePath: "coinsuranceAmount",
                    tableWidthClassName: "w-[120px]",
                },
                deductibleAmount: {
                    header: "Deductible",
                    valuePath: "deductibleAmount",
                    tableWidthClassName: "w-[120px]",
                },
            }
        },
    },
}
